<script>
  // The public media kit: logos, photos and press files anyone can download. Reached only from the footer; managed in the admin (Media kit).
  import { cutReveal } from '../lib/cutReveal.js';
  import { onMount } from 'svelte';
  import { loadStoreData } from '../lib/storeData.js';
  import Navbar from '../components/Navbar.svelte';
  import Footer from '../components/Footer.svelte';
  import Loader from '../components/Loader.svelte';

  let data = null;
  let media = { assets: [], groups: [] };
  let loading = true;
  let failed = false;
  onMount(async () => {
    try {
      const [d, m] = await Promise.all([loadStoreData(), fetch('/api/media').then(r => (r.ok ? r.json() : Promise.reject()))]);
      data = d; media = m;
    } catch { failed = true; try { data = await loadStoreData(); } catch { /* the page still renders its frame */ } }
    finally { loading = false; }
  });

  const size = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : b ? `${Math.max(1, Math.round(b / 1024))} KB` : '');
  const isImage = (a) => /^image\/(svg\+xml|png|jpeg|webp|gif)$/.test(a.mime);
  // A white logo would vanish on a light card, so anything named "white" is shown on a dark one.
  const onDark = (a) => /white|light|reverse/i.test(a.title);
  $: sections = [...(media.groups || []), ...new Set((media.assets || []).map(a => a.group).filter(g => !(media.groups || []).includes(g)))]
    .map(g => ({ name: g, items: (media.assets || []).filter(a => a.group === g) })).filter(s => s.items.length);
</script>

<svelte:head>
  <title>{data ? `Media kit — ${data.site.name}` : 'Media kit'}</title>
  <meta name="description" content="Logos, photos and press files for download." />
</svelte:head>

{#if loading || !data}
  <Loader />
{:else}
  <div class="min-h-screen flex flex-col">
    <Navbar siteName={data.site.name} logo={data.site.logo} logoHeight={data.site.navLogoSize} />
    <div class="flex-1 pt-28 pb-20 px-6 md:px-10 max-w-6xl mx-auto w-full">
      <h1 use:cutReveal class="text-3xl md:text-4xl font-display font-bold mb-3">Media kit</h1>
      <p class="text-muted-foreground max-w-2xl mb-12">Logos and brand files for press, stockists and partners. Please use the logo as it is: don't recolour, stretch or add effects to it.</p>

      {#if failed}
        <p class="text-muted-foreground">The media kit could not be loaded. Please try again in a moment.</p>
      {:else if sections.length === 0}
        <p class="text-muted-foreground">Nothing here yet.</p>
      {/if}

      {#each sections as section (section.name)}
        <section class="mb-14" aria-labelledby="media-{section.name.replace(/\W+/g, '-')}">
          <h2 id="media-{section.name.replace(/\W+/g, '-')}" class="text-label tracking-[0.2em] text-muted-foreground mb-5">{section.name.toUpperCase()}</h2>
          <ul class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {#each section.items as a (a.id)}
              <li class="border border-border">
                <div class="aspect-[4/3] flex items-center justify-center p-8 {onDark(a) ? 'bg-foreground' : 'bg-secondary'}">
                  {#if isImage(a)}
                    <img src={a.url} alt={a.title} class="max-h-full max-w-full object-contain" loading="lazy" />
                  {:else}
                    <span class="text-sm tracking-[0.2em] {onDark(a) ? 'text-primary-foreground' : 'text-muted-foreground'}">{(a.ext || 'FILE').toUpperCase()}</span>
                  {/if}
                </div>
                <div class="p-4 flex items-start justify-between gap-4">
                  <div class="min-w-0">
                    <h3 class="font-medium leading-snug">{a.title}</h3>
                    {#if a.description}<p class="text-sm text-muted-foreground mt-1">{a.description}</p>{/if}
                    <p class="text-xs text-muted-foreground mt-2">{(a.ext || '').toUpperCase()}{size(a.bytes) ? ` · ${size(a.bytes)}` : ''}</p>
                  </div>
                  <a href={a.downloadUrl} class="shrink-0 border border-border px-4 py-2 text-label tracking-[0.2em] hover:bg-foreground hover:text-primary-foreground transition-colors" aria-label="Download {a.title}">DOWNLOAD</a>
                </div>
              </li>
            {/each}
          </ul>
        </section>
      {/each}
    </div>
    <Footer {data} />
  </div>
{/if}
